import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Database, Download, Trash2, Loader2, ShieldCheck, Clock, AlertCircle,
  CheckCircle2, RefreshCcw, HardDrive, Zap,
} from "lucide-react";
import SaaSAdminLayout from "../layouts/SaaSAdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatBytes(n) {
  if (!n || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
}

const STATUS_BADGE = {
  success: "bg-emerald-100 text-emerald-700",
  running: "bg-sky-100 text-sky-700 animate-pulse",
  failed: "bg-red-100 text-red-700",
  missing: "bg-amber-100 text-amber-700",
};

export default function SaasBackup() {
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [items, setItems] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, sr] = await Promise.all([
        fetch(`${API}/saas-admin/backup/list`, { headers }).then(r => r.ok ? r.json() : { items: [] }),
        fetch(`${API}/saas-admin/backup/schedule`, { headers }).then(r => r.ok ? r.json() : null),
      ]);
      setItems(lr.items || []);
      setSchedule(sr);
    } catch (e) { showToast("error", "Failed to load backups"); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const createBackup = async () => {
    if (creating) return;
    setCreating(true);
    showToast("info", "Creating full backup… (may take a few seconds)");
    try {
      const r = await fetch(`${API}/saas-admin/backup/create`, {
        method: "POST", headers,
      });
      const d = await r.json();
      if (!r.ok) {
        showToast("error", d.detail || "Backup failed");
      } else {
        showToast("success", `Backup created · ${formatBytes(d.size_bytes)} · ${d.duration_seconds}s`);
        load();
      }
    } catch (e) { showToast("error", "Backup request failed"); }
    setCreating(false);
  };

  const downloadBackup = async (id, filename) => {
    try {
      const r = await fetch(`${API}/saas-admin/backup/download/${id}`, { headers });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        showToast("error", d.detail || "Download failed");
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "backup.archive.gz";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { showToast("error", "Download failed"); }
  };

  const deleteBackup = async (id) => {
    if (!window.confirm("Delete this backup? This cannot be undone.")) return;
    const r = await fetch(`${API}/saas-admin/backup/${id}`, { method: "DELETE", headers });
    if (r.ok) { showToast("success", "Backup deleted"); load(); }
    else showToast("error", "Delete failed");
  };

  return (
    <SaaSAdminLayout
      pageTitle="System Backup"
      pageSubtitle="Encrypted MongoDB backups · weekly auto + on-demand"
      headerRight={
        <button
          onClick={createBackup}
          disabled={creating}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm shadow-sm hover:shadow-md disabled:opacity-50"
          data-testid="backup-create-btn"
        >
          {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>
                    : <><Zap className="w-4 h-4" /> Create Full Backup</>}
        </button>
      }
    >
      <div className="space-y-6" data-testid="saas-backup-page">
        {/* Mobile-only action button (header on desktop already has it) */}
        <div className="lg:hidden">
          <button
            onClick={createBackup}
            disabled={creating}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow-sm hover:shadow-md disabled:opacity-50"
            data-testid="backup-create-btn-mobile"
          >
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>
                      : <><Zap className="w-4 h-4" /> Create Full Backup</>}
          </button>
        </div>

        {/* Status cards */}
        <div className="grid sm:grid-cols-4 gap-3" data-testid="backup-stats">
          <Card icon={CheckCircle2} label="Total backups" value={items.length} accent="from-emerald-500 to-emerald-700" />
          <Card icon={HardDrive} label="Total size"
                value={formatBytes(items.reduce((a, b) => a + (b.size_bytes || 0), 0))}
                accent="from-blue-500 to-blue-700" />
          <Card icon={Clock} label="Last backup"
                value={items[0]?.created_at ? new Date(items[0].created_at).toLocaleString() : "—"}
                accent="from-violet-500 to-violet-700" small />
          <Card icon={RefreshCcw} label={`Weekly auto · next`}
                value={schedule?.next_scheduled_at ? new Date(schedule.next_scheduled_at).toLocaleString() : "Pending first run"}
                accent="from-amber-500 to-amber-700" small />
        </div>

        {/* Security & policy strip */}
        <div className="rounded-2xl border border-blue-100 bg-blue-50/50 px-4 py-3 text-xs text-blue-800 flex flex-col sm:flex-row sm:items-center gap-2"
             data-testid="backup-policy">
          <ShieldCheck className="w-4 h-4 shrink-0" />
          <p>
            Stored at <code className="font-mono bg-white/60 px-1 rounded">{schedule?.backup_dir || "/app/backend/storage/backups"}</code> ·
            cadence <b>every {schedule?.cadence_days || 7} days</b> ·
            manual rate-limit <b>1 / {schedule?.min_manual_interval_minutes || 30} min</b> ·
            every action logged in audit_logs.
          </p>
        </div>

        {/* Backups table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="backup-list">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Database className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm">No backups yet. Click <b>Create Full Backup</b> to make your first one.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Filename</th>
                  <th className="px-4 py-2 text-left">Created</th>
                  <th className="px-4 py-2 text-left">Trigger</th>
                  <th className="px-4 py-2 text-left">Size</th>
                  <th className="px-4 py-2 text-left">Duration</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-t border-gray-100 hover:bg-blue-50/40" data-testid={`backup-row-${it.id}`}>
                    <td className="px-4 py-2.5 font-mono text-[12px] text-gray-700">
                      <div>{it.filename}</div>
                      {it.engine && (
                        <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${it.engine === "pymongo" ? "bg-violet-50 text-violet-700" : "bg-slate-50 text-slate-600"}`}>
                          engine: {it.engine}
                        </span>
                      )}
                      {(it.status === "failed" || it.status === "missing") && it.error && (
                        <p className="mt-1 text-[11px] text-red-600 font-sans whitespace-normal break-words max-w-md" data-testid={`backup-error-${it.id}`}>
                          {it.error}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">{new Date(it.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${it.trigger === "auto" ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"}`}>
                        {it.trigger}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">{formatBytes(it.size_bytes)}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{it.duration_seconds ? `${it.duration_seconds}s` : "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${STATUS_BADGE[it.status] || "bg-slate-100 text-slate-600"}`}>
                        {it.status}
                      </span>
                      {(it.status === "failed" || it.status === "missing") && it.error && (
                        <span title={it.error}><AlertCircle className="w-3.5 h-3.5 inline ml-1 text-red-500" /></span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right space-x-1">
                      <button
                        onClick={() => downloadBackup(it.id, it.filename)}
                        disabled={it.status !== "success"}
                        className="p-1.5 rounded-md hover:bg-blue-100 text-blue-600 disabled:opacity-40 disabled:hover:bg-transparent"
                        data-testid={`backup-download-${it.id}`}
                        title="Download"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteBackup(it.id)}
                        className="p-1.5 rounded-md hover:bg-red-100 text-red-500"
                        data-testid={`backup-delete-${it.id}`}
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Restore guide */}
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-xs text-gray-600">
          <p className="font-semibold text-gray-700 mb-1">↻ How to restore</p>
          <code className="font-mono bg-gray-50 px-2 py-1 rounded inline-block text-[11px]">
            mongorestore --uri="$MONGO_URL" --archive=&lt;file.archive.gz&gt; --gzip --drop
          </code>
        </div>
      </div>

      {toast && (
        <div
          className={`fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl shadow-lg text-sm font-medium text-white z-50 ${
            toast.kind === "success" ? "bg-emerald-600" :
            toast.kind === "error" ? "bg-red-600" : "bg-blue-600"
          }`}
          data-testid="backup-toast"
        >
          {toast.msg}
        </div>
      )}
    </SaaSAdminLayout>
  );
}

function Card({ icon: Icon, label, value, accent, small }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className={`${small ? "text-sm font-semibold" : "text-2xl font-bold"} mt-1 bg-gradient-to-r ${accent} bg-clip-text text-transparent`}>
        {value}
      </p>
    </div>
  );
}
